import { BookOpen, ChevronDown, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { HighlightedCodeBlock } from '@/components/ui/highlighted-code-block'
import { SyntaxHighlightedMarkdown } from '@/components/ui/syntax-highlighted-markdown'
import { cn } from '@/lib/utils'

// =============================================================================
// Types
// =============================================================================

export interface RecipeImplementation {
  language: string
  code: string
}

export interface RecipeForCallout {
  id: string
  name: string
  shortDescription: string | null
  descriptionDetails: string | null
  pseudocode: string | null
  implementations: RecipeImplementation[]
}

interface RecipeCalloutProps {
  /** The recipe to display */
  recipe: RecipeForCallout
  /** The language to show for implementation */
  language: string
  /** Optional className */
  className?: string
}

// =============================================================================
// Component
// =============================================================================

export function RecipeCallout({
  recipe,
  language,
  className,
}: RecipeCalloutProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  // Find the matching implementation for the selected language
  const implementation = recipe.implementations.find(
    (impl) => impl.language === language,
  )

  return (
    <div
      className={cn(
        'overflow-hidden rounded-lg border-2',
        'border-amber-500/30 bg-gradient-to-r from-amber-500/5 to-orange-500/5',
        className,
      )}
    >
      {/* Collapsed/Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-start gap-3 p-4 text-left transition-colors hover:bg-amber-500/5"
      >
        <BookOpen className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-amber-700 dark:text-amber-400">
              {recipe.name}
            </h4>
          </div>
          {recipe.shortDescription && (
            <p className="text-muted-foreground mt-1 text-sm line-clamp-2">
              {recipe.shortDescription}
            </p>
          )}
        </div>
        <div className="text-muted-foreground mt-0.5">
          {isExpanded ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-amber-500/20 px-4 pb-4 pt-3">
          {/* Description Details */}
          {recipe.descriptionDetails && (
            <div className="mb-4">
              <SyntaxHighlightedMarkdown className="prose prose-sm dark:prose-invert max-w-none prose-headings:text-sm prose-headings:font-medium prose-p:text-sm prose-p:leading-relaxed">
                {recipe.descriptionDetails}
              </SyntaxHighlightedMarkdown>
            </div>
          )}

          {/* Side-by-side: Pseudocode and Implementation */}
          {(recipe.pseudocode || implementation) && (
            <div className="grid gap-4 md:grid-cols-2">
              {/* Pseudocode Panel */}
              {recipe.pseudocode && (
                <div>
                  <div className="mb-2 flex items-center gap-2">
                    <div className="h-4 w-1 rounded-full bg-gradient-to-b from-amber-400 to-orange-500" />
                    <h5 className="text-sm font-medium text-amber-700 dark:text-amber-400">
                      Pseudocode
                    </h5>
                  </div>
                  <SyntaxHighlightedMarkdown className="prose prose-sm dark:prose-invert max-w-none [&>pre]:my-0 [&_pre]:text-xs">
                    {`\`\`\`\n${recipe.pseudocode}\n\`\`\``}
                  </SyntaxHighlightedMarkdown>
                </div>
              )}

              {/* Implementation Panel */}
              {implementation ? (
                <div>
                  <div className="mb-2 flex items-center gap-2">
                    <div className="h-4 w-1 rounded-full bg-gradient-to-b from-sky-400 to-blue-500" />
                    <h5 className="text-foreground text-sm font-medium">
                      {language === 'python' ? 'Python' : 'JavaScript'}
                    </h5>
                  </div>
                  <HighlightedCodeBlock
                    code={implementation.code}
                    language={language === 'python' ? 'python' : 'javascript'}
                    className="!my-0 text-xs"
                  />
                </div>
              ) : (
                <div>
                  <div className="mb-2 flex items-center gap-2">
                    <div className="h-4 w-1 rounded-full bg-gradient-to-b from-gray-400 to-gray-500" />
                    <h5 className="text-foreground text-sm font-medium">
                      {language === 'python' ? 'Python' : 'JavaScript'}
                    </h5>
                  </div>
                  <div className="flex h-24 items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 dark:border-gray-700 dark:bg-gray-900">
                    <p className="text-muted-foreground text-sm italic">
                      No {language} implementation available
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* No content message */}
          {!recipe.descriptionDetails &&
            !recipe.pseudocode &&
            !implementation && (
              <p className="text-muted-foreground text-center text-sm italic">
                No additional content available for this recipe.
              </p>
            )}
        </div>
      )}
    </div>
  )
}
